/**
 * The Recorded wall's ONE clock. Every tile follows it; none of them leads.
 *
 * Why not "tile 1 is the master": a tile in a footage gap has no time to report, and a tile
 * waiting 40 s on an on-demand transcode would stop everyone. A clock of its own keeps going
 * through both, and each tile maps it through its own offset table.
 *
 * The clock HOLDS (stays put while "playing") when `isHeld()` says a tile with footage is still
 * loading or buffering. Without the hold a slow tile never catches up: the clock would be 60 s on
 * by the time its first chunk landed, the tile would seek to catch up, that seek would start
 * another transcode, and so on. The page bounds the hold per tile, so one stuck camera does not
 * freeze the wall.
 *
 * `timeRef` is exact and read by the tiles' follow loops; `time` is the same value published
 * ~8×/s for rendering, because a 60 Hz setState would re-render six video tiles every frame.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// 8x and 16x play from keyframes only (see recordings/highSpeedPlayback), which is what makes
// them sustainable across a whole wall.
export const PLAYBACK_SPEEDS = [0.5, 1, 2, 4, 8, 16];

export interface PlaybackClock {
  timeRef: React.MutableRefObject<number>;
  time: number;
  playing: boolean;
  /** Playing AND not held — what a tile's <video> should be doing right now. */
  advancing: boolean;
  held: boolean;
  rate: number;
  /** Bumps on every explicit seek, so tiles jump at once instead of waiting to see drift. */
  seekSeq: number;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  seek: (ms: number) => void;
  skip: (deltaSec: number) => void;
  setRate: (rate: number) => void;
}

export interface PlaybackClockOptions {
  minMs: number;
  maxMs: number;
  /** A tile with footage is still loading → do not advance. */
  isHeld?: () => boolean;
  /**
   * Called when the clock is advancing through a stretch where NO camera has footage. Returns
   * where to jump (the next footage anywhere), or null to stop — an empty hour should not be
   * watched in real time.
   */
  skipGap?: (ms: number) => number | null | undefined;
}

const PUBLISH_MS = 125;

export function usePlaybackClock({ minMs, maxMs, isHeld, skipGap }: PlaybackClockOptions): PlaybackClock {
  const timeRef = useRef(minMs);
  const [time, setTime] = useState(minMs);
  const [playing, setPlaying] = useState(false);
  const [held, setHeld] = useState(false);
  const [rate, setRateState] = useState(1);
  const [seekSeq, setSeekSeq] = useState(0);

  const playingRef = useRef(false);
  const rateRef = useRef(1);
  const boundsRef = useRef({ minMs, maxMs });
  const heldFnRef = useRef(isHeld);
  const skipGapRef = useRef(skipGap);
  useEffect(() => { boundsRef.current = { minMs, maxMs }; }, [minMs, maxMs]);
  useEffect(() => { heldFnRef.current = isHeld; }, [isHeld]);
  useEffect(() => { skipGapRef.current = skipGap; }, [skipGap]);

  const clamp = (ms: number) => Math.max(boundsRef.current.minMs, Math.min(boundsRef.current.maxMs, ms));

  const seek = useCallback((ms: number) => {
    timeRef.current = clamp(ms);
    setTime(timeRef.current);
    setSeekSeq((n) => n + 1);
  }, []);

  // A new range moves the clock into it; the page decides where exactly by seeking after.
  useEffect(() => {
    if (timeRef.current < minMs || timeRef.current > maxMs) seek(minMs);
  }, [minMs, maxMs, seek]);

  const pause = useCallback(() => { playingRef.current = false; setPlaying(false); }, []);
  const play = useCallback(() => {
    // At the end of the range, Play starts over rather than doing nothing.
    if (timeRef.current >= boundsRef.current.maxMs - 500) seek(boundsRef.current.minMs);
    playingRef.current = true;
    setPlaying(true);
  }, [seek]);
  const toggle = useCallback(() => (playingRef.current ? pause() : play()), [pause, play]);
  const skip = useCallback((deltaSec: number) => seek(timeRef.current + deltaSec * 1000), [seek]);
  const setRate = useCallback((r: number) => { rateRef.current = r; setRateState(r); }, []);

  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    let lastPublish = 0;
    let wasHeld = false;
    const tick = (now: number) => {
      const dt = now - last;
      last = now;
      if (playingRef.current) {
        const h = !!heldFnRef.current?.();
        if (h !== wasHeld) { wasHeld = h; setHeld(h); }
        if (!h) {
          let next = timeRef.current + dt * rateRef.current;
          const jump = skipGapRef.current?.(next);
          if (jump === null) {
            // Nothing left to play anywhere in the range.
            playingRef.current = false;
            setPlaying(false);
          } else if (jump !== undefined && jump > next) {
            next = jump;
            setSeekSeq((n) => n + 1);
          }
          if (next >= boundsRef.current.maxMs) {
            next = boundsRef.current.maxMs;
            playingRef.current = false;
            setPlaying(false);
          }
          timeRef.current = next;
        }
      } else if (wasHeld) {
        wasHeld = false;
        setHeld(false);
      }
      if (now - lastPublish >= PUBLISH_MS) {
        lastPublish = now;
        setTime(timeRef.current);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return {
    timeRef, time, playing, held, advancing: playing && !held, rate, seekSeq,
    play, pause, toggle, seek, skip, setRate,
  };
}
